import type { CommentOutput } from "@orsane/schema/comments";
import { newCommentMessageSchema, socketEvent } from "@orsane/schema/realtime";
import type { TaskUpdateOutput } from "@orsane/schema/tasks";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ChatContainerContent, ChatContainerRoot } from "@/components/prompt-kit/chat-container";
import { useSocketMessage } from "@/lib/socket/SocketContext";
import { useTRPC } from "@/lib/trpc";
import { useChangeTaskProperty } from "@/mutations/task-mutations";
import { useSessionQuery } from "@/queries/queries";
import { useTask } from "@/queries/task-queries";
import type { ActivityItem } from "@/types/messages";
import ChatImageLightbox from "./ChatImageLightbox";
import ChatMessages from "./ChatMessages";
import NewChatMessage from "./NewChatMessage";

function _sanitizeChatHtml(html: string): string {
    // Create a temporary div to parse the HTML
    const temp = document.createElement("div");
    temp.innerHTML = html.trim();

    // Remove trailing empty <p> tags and <br> tags
    while (temp.lastChild) {
        const last = temp.lastChild;

        // Check if it's an empty <p> tag (only whitespace/no content)
        if (last.nodeName === "P" && last.textContent?.trim() === "") {
            temp.removeChild(last);
            continue;
        }

        // Check if it's a <br> tag
        if (last.nodeName === "BR") {
            temp.removeChild(last);
            continue;
        }

        // Check if it's a text node with only whitespace
        if (last.nodeType === 3 && last.textContent?.trim() === "") {
            temp.removeChild(last);
            continue;
        }

        // If we hit actual content, stop trimming
        break;
    }

    return temp.innerHTML;
}

function convertActivityToGroups(messages: CommentOutput[] | undefined, updates: TaskUpdateOutput[] = []) {
    if (messages?.length === 0 && updates.length === 0) return [];

    const sortedItems = [
        ...(messages || []).map((x) => ({ data: x, type: "message" })),
        ...updates.map((x) => ({ data: x, type: "update" })),
    ];
    sortedItems.sort((a, b) => a.data.createdAt.getTime() - b.data.createdAt.getTime());

    const activityItems: ActivityItem[] = [];
    let currentActivityItem: ActivityItem | null = null;

    for (const item of sortedItems) {
        if (item.type === "update") {
            // attribute updates create a new item every time and split message groups
            currentActivityItem = null;
            const data = item.data as TaskUpdateOutput;
            activityItems.push({
                createdAt: data.createdAt,
                id: data.id.toString(),
                messages: undefined,
                type: "update",
                update: data,
                user: data.user,
            });
        } else {
            // chat messages are grouped
            const data = item.data as CommentOutput;
            const messageTime = data.createdAt.getTime();

            // Check if we can add to the current group
            if (
                currentActivityItem &&
                currentActivityItem.user.id === data.user.id &&
                currentActivityItem.messages!.length > 0
            ) {
                // Get the time of the last message in the current group
                const lastMessageTime =
                    currentActivityItem.messages![currentActivityItem.messages!.length - 1].createdAt.getTime();

                // If the message is within 5 minutes (300,000 ms) of the last message, add to current group
                if (messageTime - lastMessageTime <= 300000) {
                    currentActivityItem.messages!.push(data);
                    continue;
                }
            }

            // Create a new group
            currentActivityItem = {
                createdAt: data.createdAt,
                type: "messagegroup",
                update: undefined,
                id: data.idempotencyKey,
                messages: [data as CommentOutput],
                user: data.user,
            };
            activityItems.push(currentActivityItem!);
        }
    }

    return activityItems;
}

export default function Chat({ taskId }: { taskId: number }) {
    const trpc = useTRPC();
    const queryClient = useQueryClient();
    const [messages, setMessages] = useState<CommentOutput[]>([]);
    const { change: changeTaskProperty } = useChangeTaskProperty();
    const { data: sessionData } = useSessionQuery();
    const { data: task } = useTask({ id: taskId });
    const chatContainerRef = useRef<HTMLDivElement>(null);

    const [
        { data: initialMessages, isLoading: isLoadingMessages },
        { data: initialUpdates, isLoading: isLoadingUpdates },
    ] = useQueries({
        queries: [
            trpc.comment.list.queryOptions(
                { taskId },
                {
                    staleTime: 0,
                    refetchOnWindowFocus: false,
                    refetchOnReconnect: false,
                }
            ),
            trpc.taskUpdates.list.queryOptions(
                { taskId },
                {
                    staleTime: 0,
                    refetchOnWindowFocus: false,
                    refetchOnReconnect: false,
                }
            ),
        ],
    });

    useEffect(() => {
        if (initialMessages) {
            setMessages(initialMessages);
        }
    }, [initialMessages]);

    const activityItems = useMemo(() => {
        if (isLoadingMessages && isLoadingUpdates) return [];
        const updates = [...(initialUpdates ?? [])];
        if (task) {
            updates.unshift({
                id: 0,
                attributeId: "created",
                attributeName: "",
                attributeType: null,
                attributeValue: null,
                taskId,
                createdAt: task.createdAt,
                user: task.createdBy,
            });
        }
        return convertActivityToGroups(messages, updates);
    }, [messages, initialUpdates, task, taskId]);

    useSocketMessage({
        event: socketEvent.comment_new,
        schema: newCommentMessageSchema,
        onConnected: (socket) => {
            socket.emit("join-task", taskId);
        },
        callback: (message) => {
            if (!messages.find((m) => m.idempotencyKey === message.comment.idempotencyKey)) {
                setMessages((prevMessages) => {
                    // Combine existing messages with new ones
                    return [...prevMessages, message.comment];
                });
            }

            queryClient.invalidateQueries(trpc.task.getActivityCount.queryOptions({ id: taskId }));
        },
    });

    const sendCommentMutation = useMutation(
        trpc.comment.send.mutationOptions({
            onSuccess: async () => {
                await changeTaskProperty(taskId, { hasActivity: true });
            },
        })
    );

    const handleSubmit = async (json: Record<string, any> | null, html: string | null) => {
        // const sanitizedValue = sanitizeChatHtml(value);
        if (sendCommentMutation.isPending) return;

        const newComment: CommentOutput = {
            comment: html ?? "",
            taskId: taskId,
            id: 0,
            idempotencyKey: crypto.randomUUID(),
            createdAt: new Date(),
            user: sessionData!.user,
        };

        setMessages((prevMessages) => {
            // Combine existing messages with new ones

            const allComments = [...prevMessages, newComment];
            return allComments;
        });

        try {
            await sendCommentMutation.mutateAsync({
                commentJson: json ?? {},
                comment: html ?? "",
                idempotencyKey: newComment.idempotencyKey,
                taskId,
            });
        } catch (error) {
            toast.error("Unsuccessful", {
                description: "Failed to send message",
            });
            console.error("Error sending message:", error);
        }
    };

    return (
        <>
            <div className="flex-1 relative mb-2">
                <div className="absolute inset-0" ref={chatContainerRef}>
                    <ChatContainerRoot className="h-full w-full p-0 chat-container">
                        <ChatContainerContent className="flex flex-col gap-2 p-4">
                            {activityItems.map((activityItem) => (
                                <ChatMessages key={`activity_${activityItem.id}`} activityItem={activityItem} />
                            ))}
                        </ChatContainerContent>
                    </ChatContainerRoot>
                </div>
                <ChatImageLightbox containerRef={chatContainerRef} />
            </div>
            <footer className="relative p-4 pt-0">
                <NewChatMessage taskId={taskId} onSubmit={handleSubmit} />
            </footer>
        </>
    );
}
